import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import * as React from 'react';
import { BetreuerWorkMonthFilter, useBetreuerWorkMonthList } from 'api/betreuerWorkMonth';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { BetreuerWorkDayEdit } from 'components/BetreuerWorkMonth/BetreuerWorkDayEdit';
import { useBetreuerOne } from 'api/betreuer';
import { weekdays, minutesToTimeString, hoursToTimeString } from 'utils/time';
import moment from 'moment-timezone';
import { formStyles } from 'styles/formStyles';
import { DefaultButton } from '@fluentui/react';
import classnames from 'classnames';
import { BetreuerWorkDayAbsentLabel } from 'utils/labels';
import { BetreuerWorkMonthEdit } from 'components/BetreuerWorkMonth/BetreuerWorkMonthEdit';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';

const theme: ITheme = getTheme();
const { palette } = theme;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    initContainer: {
        width: '100%',
        textAlign: 'center',
        marginTop: 50,
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    summaryTable: {
        width: '100%',
        borderspacing: 0,
        borderCollapse: 'collapse',
    },
    table: {
        width: '100%',
        borderspacing: 0,
        borderCollapse: 'collapse',
        marginTop: 20,
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        backgroundColor: palette.neutralLighter,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdFoot: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        backgroundColor: palette.neutralLighter,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    row: {
        selectors: {
            '&:hover': {},
            '&:nth-child(odd)': {
                backgroundColor: palette.neutralLight,
            },
            '&:nth-child(even)': {
                backgroundColor: palette.neutralLighterAlt,
            },
        },
    },
    currentDay: {
        fontWeight: 700,
    },
    futureDay: {
        fontStyle: 'italic',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdRight: {
        textAlign: 'right',
    },
    tdBorderLeft: {
        borderLeft: '1px solid ' + palette.neutralTertiary,
    },
});

interface ArbeitszeitProps {
    betrNr: number;
    workYear: number;
    workMonth: number;
    printView?: boolean;
}

export const Arbeitszeit: React.FC<ArbeitszeitProps> = ({ betrNr, workYear, workMonth, printView = false }) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const filter = React.useMemo<BetreuerWorkMonthFilter>(
        () => ({
            betrNr,
            workYear,
            workMonth,
        }),
        [betrNr, workYear, workMonth],
    );

    const betreuer = useBetreuerOne({
        nr: betrNr,
        withBetreuerWorkTimeList: true,
    });

    const betreuerWorkMonthList = useBetreuerWorkMonthList(filter);

    const [editMonth, setEditMonth] = React.useState(false);
    const [editDay, setEditDay] = React.useState<number | null>(null);

    /*
    const decoratedBetreuerWorkMonth = React.useMemo<DecoratedBetreuerWorkMonth | null>(() => {
        if (!betreuerWorkMonthList.data?.[0]) {
            return null;
        }

        const today = moment();
        const betreuerWorkMonth = betreuerWorkMonthList.data[0];

        let overallWorkTimeTotal = 0;
        let overallWorkDayTotal = 0;
        const { betreuerWorkDayList } = betreuerWorkMonth;
        const decoratedBetreuerWorkDayList: DecoratedBetreuerWorkDay[] = betreuerWorkDayList.map((betreuerWorkDay) => {
            const day = moment(`${betreuerWorkDay.workYear}-${betreuerWorkDay.workMonth}-${betreuerWorkDay.workDay}`);

            const isCurrentDay = day.isSame(today, 'day');
            const isFutureDay = day.isAfter(today, 'day');

            let workTime1: BetreuerWorkTime | undefined;
            let workTime2: BetreuerWorkTime | undefined;
            let workTimeTotal = 0;
            let workDayTotal = 0;
            if (!betreuerWorkDay.absent) {
                const workTimes = (betreuer.data?.betreuerWorkTimeList || []).filter(
                    (wt) => wt.weekday === day.isoWeekday() - 1,
                );

                workTime1 = workTimes.find((wt) => wt.block === 0);
                workTime2 = workTimes.find((wt) => wt.block === 1);

                if (workTime1?.startTime && workTime1?.stopTime) {
                    workTimeTotal += workTime1.stopTime - workTime1.startTime;
                    overallWorkTimeTotal += workTime1.stopTime - workTime1.startTime;
                }
                if (workTime2?.startTime && workTime2?.stopTime) {
                    workTimeTotal += workTime2.stopTime - workTime2.startTime;
                    overallWorkTimeTotal += workTime2.stopTime - workTime2.startTime;
                }
            }

            if (betreuerWorkDay?.startTime1 && betreuerWorkDay?.stopTime1) {
                workDayTotal += betreuerWorkDay.stopTime1 - betreuerWorkDay.startTime1;
                overallWorkDayTotal += betreuerWorkDay.stopTime1 - betreuerWorkDay.startTime1;
            }
            if (betreuerWorkDay?.break1) {
                workDayTotal -= betreuerWorkDay?.break1;
                overallWorkDayTotal -= betreuerWorkDay?.break1;
            }
            if (betreuerWorkDay?.startTime2 && betreuerWorkDay?.stopTime2) {
                workDayTotal += betreuerWorkDay.stopTime2 - betreuerWorkDay.startTime2;
                overallWorkDayTotal += betreuerWorkDay.stopTime2 - betreuerWorkDay.startTime2;
            }
            if (betreuerWorkDay?.break2) {
                workDayTotal -= betreuerWorkDay?.break2;
                overallWorkDayTotal -= betreuerWorkDay?.break2;
            }

            return {
                ...betreuerWorkDay,
                isoWeekday: day.isoWeekday(),
                workTime1,
                workTime2,
                workTimeTotal,
                workDayTotal,
                isCurrentDay,
                isFutureDay,
            };
        });

        return {
            ...betreuerWorkMonth,
            decoratedBetreuerWorkDayList,
            overallWorkTimeTotal,
            overallWorkDayTotal,
        };
    }, [betreuer.data?.betreuerWorkTimeList, betreuerWorkMonthList.data]);
     */

    const betreuerWorkMonth = React.useMemo(() => betreuerWorkMonthList.data?.[0], [betreuerWorkMonthList.data]);

    const today = moment();

    if (
        betreuer.isLoading ||
        betreuer.isFetching ||
        !betreuer.data ||
        betreuerWorkMonthList.isLoading ||
        betreuerWorkMonthList.isFetching ||
        !betreuerWorkMonthList.data
    ) {
        return <LoadingIndicator />;
    }

    if (betreuerWorkMonthList.data.length === 0) {
        return (
            <div className={styles.initContainer}>
                <div>
                    Die Arbeitszeitaufzeichnung für dieses Monat wurde noch nicht gestartet.
                    <br />
                    Zum Starten der Arbeitszeitaufzeichnung muss das Vormonat abgeschlossen sein.
                    <br />
                </div>
            </div>
        );
    }

    const summaryClassNameHead = classnames(
        styles.tdHead,
        formStyles.dataValueCenter,
        printView ? formStyles.reportSummaryPrintCell : formStyles.reportSummaryCell,
    );

    const summaryClassNameBody = classnames(
        styles.tdHead,
        formStyles.dataValueCenter,
        printView ? formStyles.reportSummaryPrintCell : formStyles.reportSummaryCell,
    );

    const timeClassNameHead = classnames(
        styles.tdHead,
        formStyles.dataValueCenter,
        printView ? formStyles.reportTimePrintCell : formStyles.reportTimeCell,
    );

    const timeClassNameBody = classnames(
        styles.tdBody,
        formStyles.dataValueCenter,
        printView ? formStyles.reportTimePrintCell : formStyles.reportTimeCell,
    );

    const timeClassNameFoot = classnames(
        styles.tdHead,
        formStyles.dataValueCenter,
        printView ? formStyles.reportTimePrintCell : formStyles.reportTimeCell,
    );

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            <h1>
                Arbeitszeit {betreuer.data?.name}, Monat {workYear}-{workMonth.toString().padStart(2, '0')}
            </h1>
            {printView && (
                <table className={formStyles.reportFilterInfo}>
                    <tr>
                        <td>Druckdatum: </td>
                        <td>{moment().format('DD.MM.YYYY HH:mm:ss')}</td>
                    </tr>
                </table>
            )}
            <table className={styles.summaryTable}>
                <thead>
                    <tr>
                        <td className={summaryClassNameHead}>Urlaub Vormonat</td>
                        <td className={summaryClassNameHead}>Urlaub Anpassung</td>
                        <td className={summaryClassNameHead}>Urlaub Aktuell</td>
                        <td className={summaryClassNameHead}>Überstunden Vormonat</td>
                        <td className={summaryClassNameHead}>Überstunden Anpassung</td>
                        <td className={summaryClassNameHead}>Überstunden Aktuell</td>
                        <td className={summaryClassNameHead}>Anmerkung</td>
                        {!printView && !betreuerWorkMonth?.isFinished && <td className={summaryClassNameHead} />}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.holidayHoursPrior, true)}
                        </td>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.holidayHoursNew, true)}
                        </td>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.holidayHoursCurrent, true)}
                        </td>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.overtimeHoursPrior, true)}
                        </td>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.overtimeHoursNew, true)}
                        </td>
                        <td className={summaryClassNameBody}>
                            {hoursToTimeString(betreuerWorkMonth?.overtimeHoursCurrent, true)}
                        </td>
                        <td className={summaryClassNameBody}>{betreuerWorkMonth?.note}</td>
                        {!printView && !betreuerWorkMonth?.isFinished && (
                            <td className={classnames(summaryClassNameBody, styles.tdRight)}>
                                <DefaultButton
                                    className={formStyles.smallButton}
                                    onClick={() => {
                                        setEditMonth(true);
                                    }}
                                    text="Bearbeiten"
                                />
                            </td>
                        )}
                    </tr>
                </tbody>
            </table>
            <table className={styles.table}>
                <thead>
                    <tr>
                        <td className={classnames(styles.tdHead, styles.tdRight)}>Tag</td>
                        <td className={styles.tdHead}>Wochentag</td>
                        <td className={classnames(timeClassNameHead, styles.tdBorderLeft)}>
                            VM Von
                            <br />
                            Soll
                        </td>
                        <td className={timeClassNameHead}>
                            VM Bis
                            <br />
                            Soll
                        </td>
                        <td className={timeClassNameHead}>
                            NM Von
                            <br />
                            Soll
                        </td>
                        <td className={timeClassNameHead}>
                            NM Bis
                            <br />
                            Soll
                        </td>
                        <td className={timeClassNameHead}>
                            Stunden
                            <br />
                            Soll
                        </td>
                        <td className={classnames(timeClassNameHead, styles.tdBorderLeft)}>
                            VM Von
                            <br />
                            Ist
                        </td>
                        <td className={timeClassNameHead}>
                            VM Bis
                            <br />
                            Ist
                        </td>
                        <td className={timeClassNameHead}>
                            VM
                            <br />
                            Pause
                        </td>
                        <td className={timeClassNameHead}>
                            NM Von
                            <br />
                            Ist
                        </td>
                        <td className={timeClassNameHead}>
                            NM Bis
                            <br />
                            Ist
                        </td>
                        <td className={timeClassNameHead}>
                            NM
                            <br />
                            Pause
                        </td>
                        <td className={timeClassNameHead}>
                            Stunden
                            <br />
                            Ist
                        </td>
                        <td className={classnames(styles.tdHead, styles.tdBorderLeft)}>Abwesend</td>
                        <td className={styles.tdHead}>Anmerkung</td>
                        {!printView && !betreuerWorkMonth?.isFinished && <td className={styles.tdHead} />}
                    </tr>
                </thead>
                {betreuerWorkMonth?.betreuerWorkDayList.map((betreuerWorkDay) => {
                    const day = moment(
                        `${betreuerWorkDay.workYear}-${betreuerWorkDay.workMonth}-${betreuerWorkDay.workDay}`,
                    );

                    const isCurrentDay = day.isSame(today, 'day');
                    const isFutureDay = day.isAfter(today, 'day');

                    return (
                        <tr
                            key={day.toISOString()}
                            className={classnames(styles.row, {
                                [styles.currentDay]: isCurrentDay,
                                [styles.futureDay]: isFutureDay,
                            })}
                        >
                            <td className={classnames(styles.tdBody, styles.tdRight)}>{betreuerWorkDay.workDay}</td>
                            <td className={classnames(styles.tdBody)}>{weekdays[day.isoWeekday() - 1]}</td>
                            <td className={classnames(timeClassNameBody, styles.tdBorderLeft)}>
                                {betreuerWorkDay.absent ? '' : minutesToTimeString(betreuerWorkDay.planStartTime1)}
                            </td>
                            <td className={timeClassNameBody}>
                                {betreuerWorkDay.absent ? '' : minutesToTimeString(betreuerWorkDay.planStopTime1)}
                            </td>
                            <td className={timeClassNameBody}>
                                {betreuerWorkDay.absent ? '' : minutesToTimeString(betreuerWorkDay.planStartTime2)}
                            </td>
                            <td className={timeClassNameBody}>
                                {betreuerWorkDay.absent ? '' : minutesToTimeString(betreuerWorkDay.planStopTime2)}
                            </td>
                            <td className={timeClassNameBody}>
                                {betreuerWorkDay.absent || betreuerWorkDay.planTotal === 0
                                    ? ''
                                    : minutesToTimeString(betreuerWorkDay.planTotal)}
                            </td>
                            <td className={classnames(timeClassNameBody, styles.tdBorderLeft)}>
                                {minutesToTimeString(betreuerWorkDay.startTime1)}
                            </td>
                            <td className={timeClassNameBody}>{minutesToTimeString(betreuerWorkDay.stopTime1)}</td>
                            <td className={timeClassNameBody}>{minutesToTimeString(betreuerWorkDay.break1)}</td>
                            <td className={timeClassNameBody}>{minutesToTimeString(betreuerWorkDay.startTime2)}</td>
                            <td className={timeClassNameBody}>{minutesToTimeString(betreuerWorkDay.stopTime2)}</td>
                            <td className={timeClassNameBody}>{minutesToTimeString(betreuerWorkDay.break2)}</td>
                            <td className={timeClassNameBody}>
                                {betreuerWorkDay.actualTotal === 0
                                    ? ''
                                    : minutesToTimeString(betreuerWorkDay.actualTotal)}
                            </td>
                            <td className={classnames(styles.tdBody, styles.tdBorderLeft)}>
                                {betreuerWorkDay.absent
                                    ? BetreuerWorkDayAbsentLabel[betreuerWorkDay.absent] || betreuerWorkDay.absent
                                    : ''}
                            </td>
                            <td className={classnames(styles.tdBody)}>{betreuerWorkDay.note}</td>
                            {!printView && !betreuerWorkMonth?.isFinished && (
                                <td className={classnames(timeClassNameBody, styles.tdRight)}>
                                    <DefaultButton
                                        className={formStyles.smallButton}
                                        onClick={() => {
                                            setEditDay(betreuerWorkDay.workDay);
                                        }}
                                    >
                                        <span
                                            className={classnames({
                                                [styles.currentDay]: isCurrentDay,
                                                [styles.futureDay]: isFutureDay,
                                            })}
                                        >
                                            Bearbeiten
                                        </span>
                                    </DefaultButton>
                                </td>
                            )}
                        </tr>
                    );
                })}
                <tfoot>
                    <tr>
                        <td className={styles.tdFoot} />
                        <td className={styles.tdFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot}>{minutesToTimeString(betreuerWorkMonth?.planTotal, true)}</td>
                        <td className={classnames(timeClassNameFoot, styles.tdBorderLeft)} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot} />
                        <td className={timeClassNameFoot}>
                            {minutesToTimeString(betreuerWorkMonth?.actualTotal, true)}
                        </td>
                        <td className={classnames(timeClassNameFoot, styles.tdBorderLeft)} />
                        <td className={styles.tdFoot} />
                        {!printView && !betreuerWorkMonth?.isFinished && <td className={styles.tdFoot} />}
                    </tr>
                </tfoot>
            </table>
            {editDay !== null && (
                <BetreuerWorkDayEdit
                    betrNr={betrNr}
                    workYear={workYear}
                    workMonth={workMonth}
                    workDay={editDay}
                    onClose={(refetch) => {
                        setEditDay(null);
                        if (refetch) {
                            betreuerWorkMonthList.refetch().then();
                        }
                    }}
                />
            )}
            {editMonth && (
                <BetreuerWorkMonthEdit
                    betrNr={betrNr}
                    workYear={workYear}
                    workMonth={workMonth}
                    onClose={(refetch) => {
                        setEditMonth(false);
                        if (refetch) {
                            betreuerWorkMonthList.refetch().then();
                        }
                    }}
                />
            )}
        </div>
    );
};
